import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { getPreset, getFontPair, DEFAULT_PRESET } from "../theme/presets";

/**
 * BrandingContext — exposes admin-controlled app branding (name, logo,
 * colours, theme preset) to every component in the tree. Loaded once on
 * mount, refetched via `refresh()` after admin edits. Also injects a
 * dynamic <style> tag so the active theme preset's CSS variables override
 * the defaults globally.
 */

const DEFAULTS = {
  brand_name: "GetOwnApp Commerce",
  short_name: "GetOwnApp",
  // `mobile_app_name` is the native-shell display name set per tenant
  // in Super Admin → Tenants → Mobile app / Firebase → "App display
  // name". It's baked into the Android/iOS home-screen icon label by
  // scripts/build_app.py, and the storefront prefers it inside the
  // Capacitor native app so header + splash + profile stay in lockstep
  // with the icon label the user sees on their home screen.
  mobile_app_name: null,
  tagline: "Your own multi-vendor marketplace.",
  logo_square_url: null,
  logo_horizontal_url: null,
  accent_color: "#0F3D91",
  theme_color: "#0F3D91",
  background_color: "#ffffff",
  // Per-preset colour overrides set via Admin → Branding → Storefront
  // theme → "Edit" on a preset card. Separate from the legacy fields
  // above (which only drive the PWA meta tags) — null means "use the
  // active preset's own colour", so an untouched tenant is unaffected.
  theme_color_overrides: { primary: null, secondary: null, background: null },
  theme_preset: DEFAULT_PRESET,
  business_mode: "multi_vendor",
  // Apple 5.2.3 kill-switch — surfaced from saas_settings via /api/settings/public.
  // Defaults to true so pre-flag deployments render normally.
  // Per-tenant Reels availability, set by super-admin on the tenant
  // record and surfaced by GET /settings/app. Optimistic default so the
  // storefront never blanks Reels while branding is still loading.
  reels_enabled: true,
};


/** The effective display name shown in-app (header, profile, splash).
 *
 * Order of preference (highest first):
 *   1. `mobile_app_name` — the tenant-specific display name set in
 *      Super Admin → Tenants → Mobile app / Firebase → "App display
 *      name". This is the value baked into the Android/iOS home-screen
 *      icon label, so the in-app header matches what the user tapped.
 *      Backend defaults this to `tenant.name` when unset, so for any
 *      properly-created tenant it always has a sensible value.
 *   2. `brand_name` — the storefront marketing brand (legacy). Falls
 *      through when `mobile_app_name` is genuinely empty.
 *   3. `DEFAULTS.brand_name` — last-resort platform default.
 *
 * Why prefer `mobile_app_name` over `brand_name` for tenants?
 * Newly-created tenants inherit the platform-default `brand_name`
 * ("GetOwnApp Commerce") in their branding doc until an admin
 * customises it. Preferring the per-tenant `mobile_app_name` means the
 * tenant's actual name is shown from day one without requiring them
 * to manually edit `brand_name`. */
export function resolveAppDisplayName(b) {
  if (!b) return DEFAULTS.brand_name;
  if (b.mobile_app_name) return b.mobile_app_name;
  return b.brand_name || DEFAULTS.brand_name;
}

/** The tenant's own branding image, in the same order `StoreHeader` uses.
 *
 * Returns `null` when the tenant has uploaded no logo at all — callers MUST
 * handle that rather than substituting a stock photo. A hardcoded fallback
 * image is never tenant-specific: it shows one vertical's marketing to every
 * other vertical (an ethnic-wear model on a grocery storefront, say).
 */
export function resolveBrandImage(b) {
  if (!b) return null;
  const url = (b.logo_horizontal_url || b.logo_square_url || "").trim();
  return url || null;
}

const BrandingContext = createContext({
  branding: DEFAULTS,
  loading: true,
  refresh: async () => {},
});


/** Pick up the branding object stashed on `window` by the
 * `bootstrapBranding()` inline script in `public/index.html`. Lets the
 * React tree hydrate with the correct theme/colors/logo on the very
 * first render — no fetch round-trip, no "default theme flash". */
function readBootstrapBranding() {
  if (typeof window === "undefined") return null;
  const b = window.__BOOTSTRAP_BRANDING__;
  if (b && typeof b === "object") return { ...DEFAULTS, ...b };
  return null;
}


/** Parse a `--kb-*` HSL triplet ("H S% L%", the format every theme
 * preset uses — see theme/presets.js) into a hex color + 0-1 lightness.
 * Returns null for anything that doesn't match. */
function hslTripletToHex(hslStr) {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(hslStr || "");
  if (!m) return null;
  const h = (parseFloat(m[1]) % 360) / 360;
  const s = parseFloat(m[2]) / 100;
  const l = parseFloat(m[3]) / 100;
  const toHex = (v) => Math.round(v * 255).toString(16).padStart(2, "0");
  if (s === 0) {
    const hex = `#${toHex(l)}${toHex(l)}${toHex(l)}`;
    return { hex, lightness: l };
  }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);
  return { hex: `#${toHex(r)}${toHex(g)}${toHex(b)}`, lightness: l };
}

/** Inverse of `hslTripletToHex` — parses a "#rrggbb" hex string (the
 * format `<input type="color">` produces) into a "H S% L%" triplet, the
 * format every `--kb-*` theme variable uses. Returns null for anything
 * that doesn't match. */
function hexToHslTriplet(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex || "");
  if (!m) return null;
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Derive a hover shade for a custom primary colour override — darkens
 * light colours, lightens near-black ones (mirrors how every hand-tuned
 * preset's own `--kb-primary-hover` relates to its `--kb-primary`, e.g.
 * kalki -7%, ajio -9%, mango (near-black) +22%). */
function deriveHoverHslTriplet(hslTriplet) {
  const m = /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/.exec(hslTriplet || "");
  if (!m) return hslTriplet;
  const [, h, s, lStr] = m;
  const l = parseFloat(lStr);
  const delta = l < 15 ? 14 : -8;
  const newL = Math.min(100, Math.max(0, l + delta));
  return `${h} ${s}% ${Math.round(newL)}%`;
}

/** Sync the native status bar (icon color + Android background) to the
 * active theme preset's actual page background, so it never renders
 * invisible icons (e.g. white-on-white). No-ops outside the Capacitor
 * native shell — including plain web tabs and older installed builds
 * that predate the `@capacitor/status-bar` plugin — since this same
 * frontend bundle is loaded live inside the mobile app too. */
async function syncNativeStatusBar(kbBgHsl) {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor?.isNativePlatform?.()) return;
    const parsed = hslTripletToHex(kbBgHsl);
    if (!parsed) return;
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Style.Light = dark icons (for light backgrounds); Style.Dark =
    // light icons (for dark backgrounds) — see @capacitor/status-bar's
    // `Style` enum docs, it's named after the icon color, not the bg.
    await StatusBar.setStyle({ style: parsed.lightness >= 0.5 ? Style.Light : Style.Dark });
    // Background color is Android-only in the plugin; iOS shows the
    // WebView through a transparent status bar (existing `.safe-top`
    // CSS already paints the right color behind the notch).
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: parsed.hex });
    }
  } catch {
    // Plugin unavailable (plain web, or an app build predating this
    // change) — never let status-bar sync break the storefront.
  }
}

/** Fade out the index.html splash overlay once the React tree is
 * painted. Idempotent — safe to call from multiple effects. */
function hideBootstrapSplash() {
  if (typeof document === "undefined") return;
  const splash = document.getElementById("kb-splash");
  if (!splash) return;
  splash.setAttribute("aria-hidden", "true");
  // Remove from the DOM after the CSS fade finishes so it doesn't
  // block pointer events on the storefront.
  setTimeout(() => { try { splash.remove(); } catch { /* ignore */ } }, 300);
}


export function BrandingProvider({ children }) {
  // Hydrate from window.__BOOTSTRAP_BRANDING__ if present so React's
  // first paint already uses tenant colors / logo / brand name. Falls
  // back to DEFAULTS for the rare case the inline fetch failed (offline).
  const bootstrap = readBootstrapBranding();
  const [branding, setBranding] = useState(bootstrap || DEFAULTS);
  // KEEP the splash visible until the React refresh() resolves —
  // *regardless* of whether bootstrap was hydrated. Why:
  //
  //   * The inline bootstrap fetch is async — it can resolve AFTER
  //     React has already mounted, in which case `readBootstrapBranding()`
  //     returned null and we'd otherwise render DEFAULTS for one
  //     paint frame ("default tenant hero flash").
  //   * Even when bootstrap IS hydrated, it may have stale or wrong-
  //     tenant data (e.g. a previous APK build that pre-dated the
  //     iter71 fix). Waiting for refresh guarantees we paint the right
  //     tenant or nothing at all — the splash absorbs the wait.
  //
  // The total wait is ~50-200ms (fetch RTT) and is hidden behind the
  // existing animated splash, so the user perceives an instant launch
  // followed by the tenant home page — not a flash through defaults.
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/app");
      setBranding({ ...DEFAULTS, ...data });
    } catch {
      setBranding((prev) => prev || DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always refresh via axios on mount — the axios interceptor injects
    // the correct `X-Tenant-Slug` header based on the URL path, which
    // the inline bootstrap script CAN miss in some race conditions
    // (e.g. when the URL changes before the inline fetch completes).
    // Re-fetching guarantees the React tree converges on the right
    // tenant's branding regardless of what the bootstrap returned.
    //
    // Defer to next tick so React finishes its initial render before
    // the fetch sets state — avoids the lint plugin's false-positive
    // about setting state synchronously during effect setup.
    const id = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply dynamic document.title base, favicon, and PWA manifest
  useEffect(() => {
    if (loading) return;

    // Update <link rel="manifest">
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    // Point the <link> to the dynamic backend manifest
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    link.href = `${apiBase}/api/settings/manifest.webmanifest`;

    // Favicon — reuse square logo when set
    if (branding.logo_square_url) {
      let fav = document.querySelector('link[rel="icon"]');
      if (!fav) {
        fav = document.createElement("link");
        fav.rel = "icon";
        document.head.appendChild(fav);
      }
      fav.href = branding.logo_square_url;
    }

    // theme-color meta
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.name = "theme-color";
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = branding.theme_color || "#7c3a4b";

    // apple-mobile-web-app-title (for iOS pinned)
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.name = "apple-mobile-web-app-title";
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = branding.short_name || resolveAppDisplayName(branding);

    // Base document.title — always sync to the current display name so
    // a brand rename takes effect immediately on every tab. Pages with
    // their own useEffect setting document.title will still override on
    // render. We prefer the native `mobile_app_name` when running inside
    // the Capacitor shell so the in-app title bar matches the icon label.
    const effectiveName = resolveAppDisplayName(branding);
    if (effectiveName) {
      document.title = effectiveName;
    }
    // Branding has been fully applied to the DOM — fade out the
    // bootstrap splash so the storefront becomes interactive.
    hideBootstrapSplash();
  }, [branding, loading]);

  // Apply the storefront theme preset by injecting a <style> tag that
  // overrides the CSS variables. Also load the preset's Google Fonts.
  useEffect(() => {
    if (loading) return;
    const preset = getPreset(branding?.theme_preset);
    // Use the tenant's chosen font pair (or the preset's recommended default).
    const fontPair = getFontPair(branding?.theme_preset, branding?.font_pair) || {
      display: preset.fonts?.display, body: preset.fonts?.body,
      googleFamilies: preset.fonts?.googleFamilies || [],
    };

    // 1. Inject CSS-variable overrides
    let style = document.getElementById("kb-theme-vars");
    if (!style) {
      style = document.createElement("style");
      style.id = "kb-theme-vars";
      document.head.appendChild(style);
    }
    // Merchant colour overrides (Admin → Branding → Storefront theme →
    // "Edit" on a preset card) layered on top of this preset's own
    // vars. Each key is independently optional — only ones the merchant
    // actually set replace the preset's default, everything else is
    // untouched, so a tenant who's never used this stays pixel-identical.
    const overrides = branding?.theme_color_overrides || {};
    const mergedVars = { ...preset.vars };
    if (overrides.primary) {
      const primaryHsl = hexToHslTriplet(overrides.primary);
      if (primaryHsl) {
        mergedVars["--kb-primary"] = primaryHsl;
        mergedVars["--kb-primary-hover"] = deriveHoverHslTriplet(primaryHsl);
      }
    }
    if (overrides.secondary) {
      const secondaryHsl = hexToHslTriplet(overrides.secondary);
      if (secondaryHsl) mergedVars["--kb-accent"] = secondaryHsl;
    }
    if (overrides.background) {
      const bgHsl = hexToHslTriplet(overrides.background);
      if (bgHsl) mergedVars["--kb-bg"] = bgHsl;
    }
    const varBlock = Object.entries(mergedVars)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join("\n");
    style.textContent = `:root {\n${varBlock}\n  --radius: ${preset.radius};\n}\n` +
      `body { font-family: ${fontPair.body}; }\n` +
      `.font-display { font-family: ${fontPair.display}; }\n`;

    // 2. Load Google Fonts for this preset+pair (idempotent, dedup by id)
    const fontsId = "kb-theme-fonts";
    let fontsLink = document.getElementById(fontsId);
    const families = (fontPair.googleFamilies || [])
      .map((f) => `family=${f}`)
      .join("&");
    const href = families
      ? `https://fonts.googleapis.com/css2?${families}&display=swap`
      : "";
    if (href) {
      if (!fontsLink) {
        fontsLink = document.createElement("link");
        fontsLink.id = fontsId;
        fontsLink.rel = "stylesheet";
        document.head.appendChild(fontsLink);
      }
      if (fontsLink.href !== href) fontsLink.href = href;
    }

    // 3. Expose the active preset on <html data-theme=...> for any
    //    preset-specific CSS overrides we may want later.
    document.documentElement.setAttribute("data-theme", branding?.theme_preset || "kalki");

    // 4. Native shell only: keep the status bar icon color/background in
    //    lockstep with this preset's actual page background.
    syncNativeStatusBar(mergedVars["--kb-bg"]);
  }, [branding, loading]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
